/**
 * CoachPlaybook - Player Utilities
 * Version: 0.5.145
 * Description: Player data helpers and position definitions
 */

import { 
  getAllPlayers as dbGetAllPlayers, 
  savePlayer as dbSavePlayer, 
  deletePlayer as dbDeletePlayer 
} from '../db';

// Basketball positions - object map for easy lookup
export const POSITIONS = {
  'PG': 'Point Guard',
  'SG': 'Shooting Guard',
  'SF': 'Small Forward',
  'PF': 'Power Forward',
  'C': 'Center'
};

// Array version for iteration
export const POSITIONS_ARRAY = [
  { id: 'PG', name: 'Point Guard', short: 'PG' },
  { id: 'SG', name: 'Shooting Guard', short: 'SG' },
  { id: 'SF', name: 'Small Forward', short: 'SF' },
  { id: 'PF', name: 'Power Forward', short: 'PF' },
  { id: 'C', name: 'Center', short: 'C' }
];

// Default player template
export const DEFAULT_PLAYER = {
  id: null,
  firstName: '',
  lastName: '',
  number: '',
  position: 'PG',
  height: '',
  weight: '',
  year: '',
  notes: '',
  jerseyTemplateId: null,
  stats: {
    gamesPlayed: 0,
    pointsPerGame: 0,
    reboundsPerGame: 0,
    assistsPerGame: 0,
    stealsPerGame: 0,
    blocksPerGame: 0,
    fieldGoalPct: 0,
    threePointPct: 0,
    freeThrowPct: 0
  }
};

// Create a new player with unique ID
export const createPlayer = (overrides = {}) => ({
  ...DEFAULT_PLAYER,
  id: `player-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  ...overrides
});

// Format player display name
export const formatPlayerName = (player, format = 'full') => {
  if (!player) return '';
  
  const first = player.firstName || '';
  const last = player.lastName || '';
  
  switch (format) {
    case 'full':
      return `${first} ${last}`.trim();
    case 'last':
      return last;
    case 'first':
      return first;
    case 'initials':
      return `${first[0] || ''}${last[0] || ''}`.toUpperCase();
    case 'lastFirst':
      if (!first && !last) return '';
      if (!first) return last;
      if (!last) return first;
      return `${last}, ${first}`;
    case 'numberName':
      return `#${player.number || ''} ${last}`.trim();
    default:
      return `${first} ${last}`.trim();
  }
};

// Format height for display
export const formatHeight = (inches) => {
  if (!inches) return '';
  const ft = Math.floor(inches / 12);
  const inch = inches % 12;
  return `${ft}'${inch}"`;
};

// Parse height string to inches
export const parseHeight = (heightStr) => {
  if (!heightStr) return null;
  
  const match = heightStr.match(/(\d+)['\-]?\s*(\d+)?/);
  if (match) {
    const feet = parseInt(match[1], 10);
    const inches = parseInt(match[2] || 0, 10);
    if (feet > 12) return feet;
    return feet * 12 + inches;
  }
  return null;
};

// Sort players by number
export const sortByNumber = (players) => {
  return [...players].sort((a, b) => {
    const numA = parseInt(a.number, 10);
    const numB = parseInt(b.number, 10);
    // Use 999 only for NaN (missing/invalid), not for 0
    const sortA = isNaN(numA) ? 999 : numA;
    const sortB = isNaN(numB) ? 999 : numB;
    return sortA - sortB;
  });
};

// Sort players by position (PG -> C)
export const sortByPosition = (players) => {
  const posOrder = { 'PG': 1, 'SG': 2, 'SF': 3, 'PF': 4, 'C': 5 };
  return [...players].sort((a, b) => {
    return (posOrder[a.position] || 6) - (posOrder[b.position] || 6);
  });
};

// Sort players by last name
export const sortByName = (players) => {
  return [...players].sort((a, b) => {
    return (a.lastName || '').localeCompare(b.lastName || '');
  });
};

// Filter players by position
export const filterByPosition = (players, position) => {
  if (!position || position === 'all') return players;
  return players.filter(p => p.position === position);
};

// Alias for filterByPosition
export const filterPlayersByPosition = filterByPosition;

// Search players by name or number
export const searchPlayers = (players, query) => {
  if (!query) return players;
  const q = query.toLowerCase();
  return players.filter(p => 
    (p.firstName || '').toLowerCase().includes(q) ||
    (p.lastName || '').toLowerCase().includes(q) ||
    (p.number || '').toString().includes(q)
  );
};

// Get starters (first 5 by position)
export const getStarters = (players) => {
  const sorted = sortByPosition(players);
  return sorted.slice(0, 5);
};

// Validate player data
export const validatePlayer = (player) => {
  const errors = [];
  
  if (!player.lastName?.trim()) {
    errors.push('Last name is required');
  }
  
  if (!player.number?.toString().trim()) {
    errors.push('Jersey number is required');
  } else {
    const num = parseInt(player.number, 10);
    if (isNaN(num) || num < 0 || num > 99) {
      errors.push('Jersey number must be between 0 and 99');
    }
  }
  
  if (!player.position) {
    errors.push('Position is required');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Check for duplicate jersey number
export const isDuplicateNumber = (players, number, excludeId = null) => {
  return players.some(p => 
    p.number?.toString() === number?.toString() && 
    p.id !== excludeId
  );
};

// Check if jersey number is available (inverse of isDuplicateNumber)
export const isJerseyNumberAvailable = (players, number, excludeId = null) => {
  return !isDuplicateNumber(players, number, excludeId);
};

// Generate roster stats summary
export const getRosterStats = (players) => {
  const count = players.length;
  const byPosition = {};
  
  Object.keys(POSITIONS).forEach(posKey => {
    byPosition[posKey] = players.filter(p => p.position === posKey).length;
  });
  
  const avgHeight = players.length > 0
    ? players.reduce((sum, p) => sum + (parseHeight(p.height) || 0), 0) / players.length
    : 0;
  
  return {
    total: count,
    byPosition,
    avgHeight: Math.round(avgHeight)
  };
};

// Calculate player stat averages
export const calculateAverages = (player) => {
  if (!player || !player.stats) return null;
  
  const stats = player.stats;
  const gp = stats.gamesPlayed || 1;
  
  return {
    ppg: stats.pointsPerGame || 0,
    rpg: stats.reboundsPerGame || 0,
    apg: stats.assistsPerGame || 0,
    spg: stats.stealsPerGame || 0,
    bpg: stats.blocksPerGame || 0,
    fgPct: stats.fieldGoalPct || 0,
    threePct: stats.threePointPct || 0,
    ftPct: stats.freeThrowPct || 0
  };
};

// Database operations - wrapper functions
export const getAllPlayers = () => dbGetAllPlayers();

export const addPlayer = async (playerData) => {
  try {
    const newPlayer = createPlayer(playerData);
    await dbSavePlayer(newPlayer);
    return { success: true, player: newPlayer };
  } catch (error) {
    console.error('Failed to add player:', error);
    return { success: false, error: error.message };
  }
};

export const updatePlayer = async (id, playerData) => {
  try {
    const updatedPlayer = { ...playerData, id };
    await dbSavePlayer(updatedPlayer);
    return { success: true, player: updatedPlayer };
  } catch (error) {
    console.error('Failed to update player:', error);
    return { success: false, error: error.message };
  }
};

export const deletePlayer = async (id) => {
  try {
    await dbDeletePlayer(id);
    return { success: true };
  } catch (error) {
    console.error('Failed to delete player:', error);
    return { success: false, error: error.message };
  }
};

export default {
  POSITIONS,
  DEFAULT_PLAYER,
  createPlayer,
  formatPlayerName,
  formatHeight,
  parseHeight,
  sortByNumber,
  sortByPosition,
  sortByName,
  filterByPosition,
  filterPlayersByPosition,
  searchPlayers,
  getStarters,
  validatePlayer,
  isDuplicateNumber,
  isJerseyNumberAvailable,
  getRosterStats,
  calculateAverages,
  getAllPlayers,
  addPlayer,
  updatePlayer,
  deletePlayer
};
